import { makeEventNamespace } from '@os/dom/events'
import { findElements, findOneElement } from '@os/dom/traversal'

/**
 * `<product-description>` — the PDP upsell tabs and single-open description
 * accordions.
 *
 * Hand-authored custom element module, included next to its markup with
 * `<script src="{{ 'product-description.js' | asset_url }}" type="module">`.
 * Shared behavior comes from the `@os/*` modules resolved by the theme import
 * map. See `docs/architecture.md`.
 *
 * Markup contract:
 * - `[data-upsell-tab]` / `[data-upsell-panel]` pairs keyed by the same value;
 *   tabs are roving (Arrow/Home/End) and toggle `aria-selected` / `aria-hidden`.
 * - `.js-accordion` contains `.js-accordion-trigger` (with `aria-expanded`) and
 *   `.js-accordion-body`; opening one closes the others.
 * - `.js-view-more` expands the accordion and sets `aria-expanded`.
 *
 * This element only toggles state (the `is-open` class and `aria-expanded`).
 * The open/close height animation is pure CSS: `.product-description__accordion-content`
 * is a grid whose `grid-template-rows` transitions between `0fr` and `1fr`
 * (see the snippet's `{% stylesheet %}`), so no JS height measurement or
 * animation library is needed.
 */

const ACCORDION_SELECTOR = '.js-accordion'
const TRIGGER_SELECTOR = '.js-accordion-trigger'
const BODY_SELECTOR = '.js-accordion-body'
const VIEW_MORE_SELECTOR = '.js-view-more'

/** The previous implementation auto-expanded a focused accordion after 200ms. */
const FOCUSIN_DELAY = 200

class ProductDescription extends HTMLElement {
  #cleanup = null

  connectedCallback() {
    if (this.#cleanup != null) {
      return
    }

    const namespace = makeEventNamespace()

    const accordions = findElements(this, ACCORDION_SELECTOR)
    const upsellTabs = findElements(this, '[data-upsell-tab]')
    const upsellPanels = findElements(this, '[data-upsell-panel]')

    if (upsellTabs.length > 0 && upsellPanels.length > 0) {
      const setActiveUpsell = (index) => {
        upsellTabs.forEach((tab) => {
          const isActive = tab.getAttribute('data-upsell-tab') === index

          tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
          tab.setAttribute('tabindex', isActive ? '0' : '-1')
        })

        upsellPanels.forEach((panel) => {
          const isActive = panel.getAttribute('data-upsell-panel') === index

          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true')
        })
      }

      upsellTabs.forEach((tab) => {
        namespace.addDirectEventListener(tab, 'click', () => {
          const index = tab.getAttribute('data-upsell-tab')

          if (!index) {
            return
          }

          setActiveUpsell(index)
        })

        namespace.addDirectEventListener(tab, 'keydown', (_, evt) => {
          if (!(evt instanceof KeyboardEvent)) {
            return
          }

          const currentIndex = upsellTabs.indexOf(tab)
          let targetIndex = -1

          if (evt.key === 'ArrowRight') {
            targetIndex = (currentIndex + 1) % upsellTabs.length
          } else if (evt.key === 'ArrowLeft') {
            targetIndex =
              (currentIndex - 1 + upsellTabs.length) % upsellTabs.length
          } else if (evt.key === 'Home') {
            targetIndex = 0
          } else if (evt.key === 'End') {
            targetIndex = upsellTabs.length - 1
          }

          if (targetIndex < 0) {
            return
          }

          evt.preventDefault()
          const targetTab = upsellTabs[targetIndex]

          if (!targetTab) {
            return
          }

          const targetTabIndex = targetTab.getAttribute('data-upsell-tab')

          if (targetTabIndex) {
            setActiveUpsell(targetTabIndex)
            targetTab.focus()
          }
        })
      })

      const initialTab = upsellTabs.find(
        (tab) => tab.getAttribute('aria-selected') === 'true'
      )

      if (initialTab) {
        const initialIndex = initialTab.getAttribute('data-upsell-tab')

        if (initialIndex) {
          setActiveUpsell(initialIndex)
        }
      }
    }

    accordions.forEach((accordion) => {
      const trigger = findOneElement(accordion, TRIGGER_SELECTOR)
      const content = findOneElement(accordion, BODY_SELECTOR)
      const viewMoreBtn = findOneElement(accordion, VIEW_MORE_SELECTOR)

      if (!content || !trigger) {
        return
      }

      if (viewMoreBtn) {
        namespace.addDirectEventListener(viewMoreBtn, 'click', (_, evt) => {
          evt.preventDefault()
          evt.stopPropagation()

          accordion.classList.add('is-expanded')
          viewMoreBtn.setAttribute('aria-expanded', 'true')
        })
      }

      namespace.addDirectEventListener(accordion, 'focusin', () => {
        setTimeout(() => {
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

          if (isExpanded) {
            return
          }

          trigger.click()
        }, FOCUSIN_DELAY)
      })

      namespace.addDirectEventListener(trigger, 'mousedown', (_, evt) => {
        evt.preventDefault()
      })

      namespace.addDirectEventListener(trigger, 'click', (_, evt) => {
        evt.preventDefault()
        const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

        accordions.forEach((otherAccordion) => {
          if (otherAccordion === accordion) {
            return
          }

          const otherTrigger = findOneElement(otherAccordion, TRIGGER_SELECTOR)

          if (!otherTrigger) {
            return
          }

          if (otherTrigger.getAttribute('aria-expanded') === 'true') {
            otherTrigger.setAttribute('aria-expanded', 'false')
            otherAccordion.classList.remove('is-open')
          }
        })

        trigger.setAttribute('aria-expanded', `${!isExpanded}`)
        accordion.classList.toggle('is-open', !isExpanded)
      })
    })

    this.#cleanup = () => {
      namespace.destroy()
    }
  }

  disconnectedCallback() {
    this.#cleanup?.()
    this.#cleanup = null
  }
}

// Sections can be re-rendered by the theme editor; defining twice throws.
if (!customElements.get('product-description')) {
  customElements.define('product-description', ProductDescription)
}
